import '../styles/globals.css';
import 'react-toastify/dist/ReactToastify.css';
import '@schedule-x/theme-default/dist/index.css';

import { GoogleTagManager } from '@next/third-parties/google';
import { SessionProvider, useSession, signIn } from 'next-auth/react';

import { ApolloProvider } from '@apollo/client';
import { useRouter } from 'next/router';
import React from 'react';
import DefaultThemeProvider from '@web-packages/themes';

// @ts-ignore
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';

import type { AppProps } from 'next/app';
import ToastProvider from '@web-packages/hooks/useToast';

import { useStore } from '@shared/core-library/src/globalStore';
import { apolloClient } from '../graphql';
import { AuthClientProvider } from '@shared/core-library/src/authClientContext';
import { authClient } from '../lib/auth-client';
import { CustomNovuProvider } from '@web-packages/containers/Novu';
import { PortalRealtimeProvider, PortalRealtimeProviderProps } from '@web-libs/portal-realtime-builder/usePortalRealtime';
import { PostHogProvider } from '../services/providers'

import "@fontsource/roboto/400-italic.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500-italic.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700-italic.css";
import "@fontsource/roboto/700.css";

NProgress.configure({ showSpinner: false });

function AuthBridge({ children }: { children: React.ReactNode }) {
  const { status } = useSession();
  const [ready, setReady] = React.useState(false);
  const attempted = React.useRef(false);

  React.useEffect(() => {
    if (status === 'loading') return;
    if (status === 'authenticated') { setReady(true); return; }
    if (attempted.current) return;
    attempted.current = true;

    authClient.getSession().then(async betterAuthSession => {
      if (betterAuthSession.data) {
        await signIn('credentials', { isBetterAuth: true, redirect: false });
      }
      setReady(true);
    });
  }, [status]);

  if (!ready) return null;
  return <>{children}</>;
}

function updateChatVisibility() {
  const closedAt = localStorage.getItem('closedAt');
  const chatBox = document.getElementById('vngageTextbox');

  if (chatBox) {
    if (closedAt) {
      const closedDate = new Date(closedAt);
      const currentDate = new Date();

      const differenceInDays = Math.ceil(
        (currentDate.getTime() - closedDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (differenceInDays >= 3) {
        chatBox.style.display = '';
      } else {
        chatBox.style.display = 'none';
      }
    } else {
      chatBox.style.display = '';
    }
  }
}

// const refTimer = React.createRef<any>();
const App = ({ Component, pageProps: { session, ...pageProps } }: AppProps) => {
  const router = useRouter();
  React.useEffect(() => {
    const closeButtonListener = document.getElementById('vngageCloseTxt');
    const handleClose = () => {
      const today = new Date();
      localStorage.setItem('closedAt', today.toISOString());
      updateChatVisibility();
    };
    closeButtonListener?.addEventListener('click', handleClose);

    const handleStart = () => {
      NProgress.start();
    };

    const handleStop = () => {
      // resets store when navigating with pageLayoutProps changed.
      if (useStore.getState().pageLayoutProps) {
        useStore.setState(() => ({
          pageLayoutProps: null,
        }));
      }

      NProgress.done();
      updateChatVisibility();
    };

    updateChatVisibility();
    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleStop);
    router.events.on('routeChangeError', handleStop);

    return () => {
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleStop);
      router.events.off('routeChangeError', handleStop);
      closeButtonListener?.removeEventListener('click', handleClose);
    };
  }, [router]);

  const { disableGlobalFields = true } = pageProps?.pageLayoutProps ?? {};
  return (
    <SessionProvider session={session}>
      <AuthBridge>
        <ToastProvider>
          <AuthClientProvider client={authClient}>
            <ApolloProvider client={apolloClient}>
              <PortalRealtimeProvider
                serverEnv={process.env.ENV_NAME as PortalRealtimeProviderProps['serverEnv']}
                router={router}
                disabled={disableGlobalFields === false ? false : true}
              >
                <DefaultThemeProvider>
                  <PostHogProvider>
                    <CustomNovuProvider>
                      {React.createElement(Component, pageProps)}
                      <GoogleTagManager gtmId='GTM-W23TVB4' />
                    </CustomNovuProvider>
                  </PostHogProvider>
                </DefaultThemeProvider>
              </PortalRealtimeProvider>
            </ApolloProvider>
          </AuthClientProvider>
        </ToastProvider>
      </AuthBridge>
    </SessionProvider >
  );
};

export default App;
